import { useData } from '../../contexts/DataContext';
import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import { Link as ScrollLink } from 'react-scroll'
import { FaTools } from 'react-icons/fa';
import PageHero from '../../components/PageHero';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import CountryDisplay from '../../components/Common/CountryDisplay';

const Projects = () => {
  const { t } = useTranslation();
  const { language, updateDocumentTitle } = useLanguage();
  const { data } = useData();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState({});
  const isRTL = language === 'ar';
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('projects');

  const projects = Array.isArray(data?.projects)
    ? data.projects
    : Object.values(data?.projects || {});

  // Set loading state based on data availability
  useEffect(() => {
    if (data) {
      setLoading(false);
    }
  }, [data]);

  // Handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setIsScrolled(scrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolled]);


  useEffect(() => {
    if (data.site_info) {
      updateDocumentTitle(t('projects.title'), data.site_info);
    }
  }, [data, t, language, updateDocumentTitle]);

  // Navigation handler
  const handleNavigation = useCallback((item) => {
    setIsOpen(false);

    if (item.href?.startsWith('/project/')) {
      // First navigate to the project page
      navigate(item.href);
      // Then scroll to top
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }

    if (item.to) {
      if (location.pathname !== '/') {
        navigate('/');
        requestAnimationFrame(() => {
          const element = document.getElementById(item.to);
          if (element) {
            const yOffset = -100;
            const yPosition = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: yPosition, behavior: 'smooth' });
            setActiveSection(item.to);
          }
        });
      } else {
        const element = document.getElementById(item.to);
        if (element) {
          const yOffset = -100;
          const yPosition = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: yPosition, behavior: 'smooth' });
          setActiveSection(item.to);
        }
      }
    } else if (item.href) {
      navigate(item.href);
      setActiveSection(item.href);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.pathname, navigate]);

  // Link content component
  const LinkContent = ({ item }) => (
    <div className="flex items-center">
      <span>{item.title}</span>
      {item.icon && <FontAwesomeIcon icon={item.icon} className="ml-2" />}
    </div>
  );

  // Modified NavigationItem component
  const NavigationItem = ({ item, className, children }) => {
    const location = useLocation();
    const isHomePage = location.pathname === '/';

    const handleClick = (e) => {
      e.preventDefault();
      if (item.href?.startsWith('/project/')) {
        navigate(item.href);
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      } else {
        handleNavigation(item);
      }
    };

    return (
      <div
        onClick={handleClick}
        className={className}
        role="button"
        tabIndex={0}
      >
        {children}
      </div>
    );
  };

  // Update handleNavigation to handle scroll restoration
  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => {
        const element = document.getElementById(location.state.scrollTo);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }
        // Clear the state
        navigate(location.pathname, {
          replace: true,
          state: {}
        });
      }, 100);
    }
  }, [location.state, navigate]);

  // Loading image component
  const LoadingImage = ({ src, alt, className, index, type }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    
    return (
      <div className="relative w-full min-h-[350px] max-h-[350px]"> {/* Add minimum height container */}
        {!isLoaded && (
          <div className="absolute inset-0 bg-gray-200 rounded-lg min-h-[350px]"> {/* Add minimum height to placeholder */}
            <div className="flex justify-center items-center h-full">
              <svg className="w-8 h-8 text-gray-300" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
          </div>
        )}
        <img
          src={src}
          alt={alt}
          className={`${className} ${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300 w-full min-h-[350px] max-h-[350px] object-cover`}
          onLoad={() => setIsLoaded(true)}
        />
      </div>
    );
  };

  // Create learn more item
  const createLearnMoreItem = (project) => ({
    id: `project-${project.slug}`,
    title: t('learn_more'),
    href: `/project/${project.slug}`,
    icon: faArrowRight
  });

  // Add this helper function near your other handlers
  const handleProjectClick = (slug) => {
    navigate(`/project/${slug}`);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="relative">
      {loading && (
        <div className="flex absolute inset-0 z-50 justify-center items-center bg-white bg-opacity-75">
          <div className="w-12 h-12 rounded-full border-t-2 border-b-2 border-blue-500 animate-spin"></div>
        </div>
      )}

      <PageHero type="projects" />

      <section className="py-12 bg-gray-50">
        <div className="container px-4 mx-auto">
          <h1 className="mb-6 text-3xl font-bold">{t('projects.selected_projects')}</h1>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {projects.map((project, index) => (
              <NavigationItem
                key={project.slug}
                item={{
                  href: `/project/${project.slug}`
                }}
                className="block h-full cursor-pointer group"
              >
                <div className={`flex flex-col h-full bg-white rounded-lg overflow-hidden ${project.has_shadow ? 'shadow-lg' : ''
                  } transition-transform duration-300 group-hover:transform group-hover:scale-105`}>
                  <div className="relative flex-shrink-0">
                    <LoadingImage
                      src={project.hero_image}
                      alt={project.title}
                      className="object-cover w-full h-full"
                      index={index}
                      type="project-card"
                    />
                    <div className="absolute top-4 right-4">
                      <span className={`px-3 py-1 text-sm font-semibold rounded-full ${project.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                        {t(`projects.${project.status}`)}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col flex-grow p-6">
                    <div className="flex-grow">
                      <h3 className="mb-2 text-xl font-bold text-gray-900">{project.title}</h3>
                      <p className="mb-4 text-gray-600">{project.description.slice(0, 200)}</p>

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">{t('projects.type')}</p>
                          <p className="font-semibold">{project.type}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">{t('projects.location')}</p>
                <CountryDisplay 
                      countryCode={project.country_code}
                      city={project.city}
                    />
                        </div>
                        <div>
                          <p className="text-gray-500">{t('projects.year')}</p>
                          <p className="font-semibold">{project.year}</p>
                        </div>
                        {project.cost > 0 && (
                        <div>
                          <p className="text-gray-500">{t('projects.cost')}</p>
                          <p className="font-semibold">{Number(project.cost).toLocaleString()} {project.currency}</p>
                        </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </NavigationItem>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projects;